import { Button, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import { track } from 'shared/util/analytics'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import CopyIcon from '@material-ui/icons/FileCopy'
import ListIcon from '@material-ui/icons/List'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import React, { useMemo } from 'react'
import type { RenderableItem } from '@paintscout/util/builder'
import { getItems, getObjectLabels, getQuoteOptions, getFeatures, setQuoteOptions } from '@paintscout/util/builder'
import get from 'lodash/get'
import { hasCustomTotal } from '@paintscout/util/'
import { canSaveItemToSettings, saveToSettingsAction } from '../util/saveToSettingsAction'
import { useQuote } from '../context'
import AmountHeaderLabel from './AmountHeaderLabel'
import AreaSubstrateToggle from './AreaSubstrateToggle'
import HideAmountButton from './HideAmountButton'
import ItemRowContent from './ItemRowContent'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'

export interface BidTableNextProps {
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onDeselectItem?: (args: {
    section: 'bid' | 'options' | 'additional' | 'pending' | 'archived' | 'all'
    key?: string
  }) => void
  onItemAction?: (action: string, section: 'bid', item: RenderableItem) => void
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {},
    // Toolbar above the card. The Area/Surface view toggle always sits on the left, and Swap Products
    // on the right when no items are selected. (Once items are selected, swap lives in the
    // bulk-actions bar instead, and the toggle must not move — #24431.)
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      minHeight: 32
    },
    swapButton: {
      fontFamily: theme.typography.button.fontFamily,
      fontWeight: 500,
      fontSize: theme.typography.pxToRem(14),
      color: theme.paintscout.palette.brand.Neutral.P35,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P97
      }
    }
  }),
  { name: 'BidTableNext' }
)

/**
 * The "Item" (bid) section for the new (estimateNext) quote pipeline, rendered in the outlined
 * card style. Ported from `BidTable` — same item building, action arrays, amount toggle, and
 * Area/Surface toggle — but rendered through `OutlinedItemTable`, reusing `ItemColumn` for the row
 * content and `ActionColumn` for the per-row kebab. The Area/Surface toggle sits above the card and
 * the Hide-amount switch lives in the card header.
 */
export default function BidTableNext({
  onItemAction,
  onDeselectItem,
  selectedItems,
  onItemSelection
}: BidTableNextProps) {
  const classes = useStyles()
  const {
    quote,
    tableView,
    isEditable,
    isNoteEditable,
    onReorder,
    onTableViewChange,
    onAddItem,
    updateQuote,
    onItemAction: contextItemAction,
    selectedItemKeys = []
  } = useQuote()
  const { options } = useClientOptions()
  const { isCrew } = useUser()
  const { permissionToWriteAllDocuments, permissionToEditAccountSettings } = useUserPermissions()
  const isInvoice = !!quote.is_invoice

  const features = getFeatures({ options })
  const hasAdditionalWork = get(features, 'quotes.additionalWork')
  const canConvertToLineItem = get(features, 'quotes.convertToLineItem')
  const canFullyEdit = isEditable || !quote.is_invoice || permissionToWriteAllDocuments || !hasAdditionalWork
  const { showPrices, showDetailedBreakdown, allowShowCustomizedTotalPrice, addIndex, hideAllPrices, serviceFees } =
    getQuoteOptions({ quote, options })

  const objectLabels = getObjectLabels({ options, invoice: isInvoice })
  const disableSubstrateView = hasCustomTotal(quote.areas)
  const items: RenderableItem[] = useMemo(
    () =>
      getItems({ quote, section: 'bid', view: tableView, consumer: 'customer', options }).map((item) => {
        if (showDetailedBreakdown && !item.description && !item.value && item.subItems && item.subItems.length > 0) {
          const firstSubItem = item.subItems.shift()
          return {
            ...item,
            description: { useCustom: false, default: firstSubItem.description, custom: '' },
            value: firstSubItem.value
          }
        }
        return item
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, tableView]
  )

  if (items.length === 0 && !isEditable) {
    return null
  }

  // Both toolbar controls act on existing items, so neither belongs on an estimate that has none —
  // a new estimate shows the item card and nothing else (ENG-1478).
  const hasItems = items.length > 0
  const showSwap = isEditable && selectedItemKeys.length === 0 && hasItems

  const allowAmountToggle =
    !quote.totalAdjustment ||
    allowShowCustomizedTotalPrice ||
    (quote.totalAdjustment && quote.totalAdjustment.rate === 0)
  const activeServiceFee = serviceFees?.hiddenValue && serviceFees.hiddenValue > 0
  const amountToggleDisabledReason = activeServiceFee
    ? 'A service fee is included in the sub-total, so prices for each item cannot be shown when using it.'
    : !allowAmountToggle
      ? 'Prices for each item cannot be shown because the total price has been customized.'
      : undefined

  function handleReorder({ items, addIndex }) {
    onReorder({ items, section: 'bid', view: tableView, addIndex })
  }

  function handleTableViewChange(_event: any, view: string) {
    onTableViewChange(view)
    if (onDeselectItem) {
      onDeselectItem({ section: 'all' })
    }
  }

  function handleToggleAmount(_ev: any) {
    const quoteOptions = getQuoteOptions({ options, quote })
    quoteOptions.showPrices = !quoteOptions.showPrices
    const updatedQuote = setQuoteOptions({ quoteOptions, quote })
    updateQuote({ quote: updatedQuote })
  }

  // Right side of the navy "Item" band: the amount toggle in the editor, and — for the customer, who
  // has nothing to toggle — the currency symbol over the price column, matching the legacy `BidTable`
  // head (AmountHeadColumn with `consumer: 'customer'` and no switch).
  const headerRight =
    items.length === 0 || hideAllPrices ? undefined : isEditable ? (
      <HideAmountButton
        onDark
        visibleLabel="Hide Price"
        hiddenLabel="Show Price"
        disabled={!allowAmountToggle || activeServiceFee}
        disabledTooltip={amountToggleDisabledReason}
        onToggle={handleToggleAmount}
        visible={showPrices && !hideAllPrices}
      />
    ) : showPrices ? (
      <AmountHeaderLabel onDark>{objectLabels.currency.symbol}</AmountHeaderLabel>
    ) : undefined

  return (
    <div className={classes.root}>
      {isEditable && hasItems && (
        <div className={classes.toolbar} data-testid="bid-toolbar">
          <AreaSubstrateToggle view={tableView} onChange={handleTableViewChange} disabled={disableSubstrateView} />
          {showSwap && (
            <Button
              className={classes.swapButton}
              variant="text"
              size="small"
              icon={SwapHorizIcon}
              onClick={() => {
                track('product_swap_opened', { source: 'bid_toolbar' })
                contextItemAction('swap-products', 'bid', [])
              }}
            >
              Swap Products
            </Button>
          )}
        </div>
      )}

      <OutlinedItemTable
        title={'Item'}
        headerRight={headerRight}
        headerVariant="dark"
        draggable={!!isEditable}
        checkboxes={isEditable}
        showAddButton={isEditable}
        items={items}
        selectedItems={selectedItems}
        addIndex={addIndex.bid}
        addButtonLabel={'Add Area or Line Item'}
        section={'bid'}
        onReorder={handleReorder}
        onItemSelection={onItemSelection}
        onItemClick={
          isEditable || isNoteEditable ? (_ev, item) => onItemAction('edit', 'bid', item as RenderableItem) : undefined
        }
        singleBorder
        onAddClick={(_ev, insertBeforeKey, type) => onAddItem('bid', insertBeforeKey, type)}
        renderRowColumns={(item: RenderableItem) => {
          const actions = []

          actions.push({ key: 'edit', label: 'Edit', icon: EditIcon })

          if (canFullyEdit) {
            actions.push({ key: 'make-option', label: `Make ${objectLabels.option.value}`, icon: AddBoxIcon })

            // Duplicate sits above Archive, per the prototype (ENG-1922).
            if (tableView === 'area') {
              actions.push({ key: 'copy', label: 'Duplicate', icon: CopyIcon })
            }
            actions.push({ key: 'archive', label: 'Archive', icon: ArchiveIcon })

            if (item.type === 'group') {
              actions.push({ key: 'ungroup', label: 'Ungroup', icon: ListIcon })
            }
            if (canConvertToLineItem && item.type !== 'line_item') {
              actions.push({ key: 'convert', label: 'Convert to Line Item', icon: SwapHorizIcon })
            }

            if (canSaveItemToSettings({ permissionToEditAccountSettings, item })) {
              actions.push(saveToSettingsAction)
            }

            // Last, as the one destructive action (ENG-1922). The "Are you sure?" prompt comes from
            // the context — `handleItemAction('delete')` opens a ConfirmationDialog before removing
            // anything — so there is nothing to confirm here.
            actions.push({ key: 'delete', label: 'Delete', icon: DeleteIcon })
          }

          return (
            <ItemRowContent
              item={item}
              section={'bid'}
              isEditable={isEditable || isNoteEditable}
              showAmount={showPrices}
              actions={isEditable && !isCrew ? actions : undefined}
              onAction={(action, it) => onItemAction(action, 'bid', it)}
            />
          )
        }}
      />
    </div>
  )
}
